import { useCallback, useEffect, useRef, useState } from "react";
import AppLayout from "@/components/feature/AppLayout";
import DateRangePicker from "@/components/feature/DateRangePicker";
import { useWorkspace } from "@/contexts/WorkspaceContext";
import { useReportingDashboard } from "./hooks/useReportingDashboard";
import { useReportingChats } from "./hooks/useReportingChats";
import { useReportingBot } from "./hooks/useReportingBot";
import BotAnalyticsPanel from "./components/BotAnalyticsPanel";
import HumanAgentQaSection from "./components/HumanAgentQaSection";
import { KpiGrid, ShareList, VolumeBars } from "./components/ReportingChartBlocks";

const AVATAR_BY_NAME = {
  Neelov: "https://readdy.ai/api/search-image?query=professional%20man%20portrait%20smiling%20friendly%20face%20neutral%20background%20corporate%20headshot&width=40&height=40&seq=tp1&orientation=squarish",
  "Alex Kim": "https://readdy.ai/api/search-image?query=professional%20asian%20man%20portrait%20smiling%20friendly%20face%20neutral%20background%20corporate%20headshot&width=40&height=40&seq=tp2&orientation=squarish",
  "Maria Santos": "https://readdy.ai/api/search-image?query=professional%20latina%20woman%20portrait%20smiling%20friendly%20face%20neutral%20background%20corporate%20headshot&width=40&height=40&seq=tp3&orientation=squarish",
  "Jake Turner": "https://readdy.ai/api/search-image?query=professional%20young%20man%20portrait%20smiling%20friendly%20face%20neutral%20background%20corporate%20headshot&width=40&height=40&seq=tp4&orientation=squarish",
};

function AgentAvatar({ name }) {
  const src = AVATAR_BY_NAME[name];
  if (src) {
    return <img src={src} alt="" className="h-9 w-9 rounded-full object-cover ring-2 ring-white shadow-md" />;
  }
  const initial = String(name || "?")
    .split(/\s+/)
    .map((p) => p[0])
    .join("")
    .slice(0, 2)
    .toUpperCase();
  return (
    <div className="flex h-9 w-9 items-center justify-center rounded-full bg-slate-200 text-xs font-bold text-slate-600 ring-2 ring-white shadow-md">
      {initial}
    </div>
  );
}


const REPORT_TABS = [
  {
    id: "chats",
    label: "Live chat",
    short: "Chats",
    description: "Conversation volume, assignees, chat SLA risk, transfer handoffs, and agent QA scores.",
    icon: "ri-chat-3-line",
    accent: "teal",
  },
  {
    id: "tickets",
    label: "Tickets",
    short: "Tickets",
    description: "Queue depth, status mix, ticket SLA risk, and agent load from linked conversations.",
    icon: "ri-ticket-2-line",
    accent: "amber",
  },
  {
    id: "bot",
    label: "Chatbot",
    short: "Bot",
    description: "Widget bot volume, completion, handoffs, CSAT, sentiment, trending topics, and AI QA coaching.",
    icon: "ri-robot-2-line",
    accent: "violet",
  },
];

const REPORT_TAB_ORDER = ["chats", "tickets", "bot"];

export default function AnalyticsPage() {
  const { data: ticketsDashData, loading: ticketsLoading, error: ticketsError, refresh: refreshTickets } =
    useReportingDashboard();
  const { chats: chatsFromHook, loading: chatsLoading, error: chatsError, refresh: refreshChats } = useReportingChats();
  const { bot: botFromHook, loading: botLoading, error: botError, refresh: refreshBot } = useReportingBot();
  const { activeWorkspaceId, loading: workspaceLoading } = useWorkspace();
  const [reportView, setReportView] = useState(/** @type {'chats' | 'tickets' | 'bot'} */ ("chats"));
  const scrollRef = useRef(/** @type {HTMLDivElement | null} */ (null));

  const refresh = useCallback(() => {
    void refreshTickets();
    void refreshChats();
    void refreshBot();
  }, [refreshTickets, refreshChats, refreshBot]);

  const tickets = ticketsDashData?.tickets && typeof ticketsDashData.tickets === "object" ? ticketsDashData.tickets : {};
  const ticketKpis = Array.isArray(tickets.kpis) ? tickets.kpis : [];
  const ticketStatusShare = Array.isArray(tickets.ticketStatusShare) ? tickets.ticketStatusShare : [];
  const statusCounts = Array.isArray(tickets.statusCounts) ? tickets.statusCounts : [];
  const agentTicketRows = Array.isArray(tickets.agentTicketRows) ? tickets.agentTicketRows : [];
  const ticketVolumeWeek = Array.isArray(tickets.volumeWeek) ? tickets.volumeWeek : [];
  const ticketVolumeMax = typeof tickets.volumeMax === "number" ? tickets.volumeMax : 1;
  const ticketMeta = tickets.meta && typeof tickets.meta === "object" ? tickets.meta : {};

  const chats = chatsFromHook;
  const chatKpis = Array.isArray(chats.kpis) ? chats.kpis : [];
  const assigneeBars = Array.isArray(chats.assigneeBars) ? chats.assigneeBars : [];
  const maxAssignee = typeof chats.maxAssignee === "number" ? chats.maxAssignee : 1;
  const chatStatusShare = Array.isArray(chats.chatStatusShare) ? chats.chatStatusShare : [];
  const agentChatRows = Array.isArray(chats.agentChatRows) ? chats.agentChatRows : [];
  const chatVolumeWeek = Array.isArray(chats.volumeWeek) ? chats.volumeWeek : [];
  const chatVolumeMax = typeof chats.volumeMax === "number" ? chats.volumeMax : 1;
  const chatMeta = chats.meta && typeof chats.meta === "object" ? chats.meta : {};
  const volumeEmpty = Boolean(chats.volumeEmpty);

  const refreshBusy = chatsLoading || ticketsLoading || botLoading;
  const surfaceError =
    reportView === "chats" ? chatsError : reportView === "tickets" ? ticketsError : botError;
  const noWorkspace = !workspaceLoading && !activeWorkspaceId;

  const chatSkeleton =
    reportView === "chats" && Boolean(activeWorkspaceId) && chatsLoading && !chatsError;
  const ticketSkeleton = reportView === "tickets" && ticketsLoading && !ticketsDashData;
  const botSkeleton = reportView === "bot" && Boolean(activeWorkspaceId) && botLoading && !botError;

  const showChatsPanel = reportView === "chats" && !chatSkeleton;
  const showTicketsPanel = reportView === "tickets" && !ticketSkeleton;
  const showBotPanel = reportView === "bot" && !botSkeleton;

  const activeTab = REPORT_TABS.find((t) => t.id === reportView) ?? REPORT_TABS[0];

  useEffect(() => {
    if (scrollRef.current) scrollRef.current.scrollTop = 0;
  }, [reportView]);

  const onTabButtonKeyDown = useCallback((e) => {
    if (e.key !== "ArrowRight" && e.key !== "ArrowLeft") return;
    e.preventDefault();
    setReportView((v) => {
      const idx = REPORT_TAB_ORDER.indexOf(v);
      const next = e.key === "ArrowRight" ? idx + 1 : idx - 1;
      const wrapped = (next + REPORT_TAB_ORDER.length) % REPORT_TAB_ORDER.length;
      return /** @type {'chats' | 'tickets' | 'bot'} */ (REPORT_TAB_ORDER[wrapped]);
    });
  }, []);

  return (
    <AppLayout>
      <div className="ui-page scrollbar-thin flex h-full min-h-0 flex-col">
        <header className="ui-shell relative z-20 shrink-0 flex flex-col gap-4 sm:flex-row sm:items-center sm:justify-between">
          <div>
            <p className="text-xs font-semibold uppercase tracking-widest text-teal-700">Insights</p>
            <h1 className="mt-1 text-2xl font-semibold tracking-tight text-slate-900 sm:text-3xl">Reporting</h1>
            <p className="mt-1 max-w-xl text-sm text-slate-500">
              One place for operations metrics. Pick a scope below — defaults to live chat so managers land on the busiest surface first.
            </p>
          </div>
          <div className="flex flex-wrap items-center gap-2">
            <DateRangePicker />
            <button type="button" onClick={() => void refresh()} className="ui-btn-primary !py-2.5 !text-sm" disabled={refreshBusy}>
              {refreshBusy ? "Refreshing…" : "Refresh data"}
            </button>
          </div>
        </header>

        <div className="relative z-10 flex min-h-0 flex-1 flex-col overflow-hidden">
          <div className="shrink-0 border-b border-slate-200/80 bg-slate-50/90 px-4 py-3 backdrop-blur-md sm:px-8">
            <div className="mx-auto max-w-screen-2xl">
              <p id="report-scope-label" className="mb-2 text-[10px] font-bold uppercase tracking-widest text-slate-400">
                What to review
              </p>
              <div className="flex flex-col gap-3 sm:flex-row sm:items-center sm:justify-between">
                <div className="inline-flex rounded-2xl bg-slate-100/90 p-1 ring-1 ring-slate-200/60" role="tablist" aria-labelledby="report-scope-label">
                  {REPORT_TABS.map((tab) => {
                    const selected = reportView === tab.id;
                    return (
                      <button
                        key={tab.id}
                        type="button"
                        role="tab"
                        aria-selected={selected}
                        id={`report-tab-${tab.id}`}
                        onKeyDown={onTabButtonKeyDown}
                        onClick={() => setReportView(/** @type {'chats' | 'tickets' | 'bot'} */ (tab.id))}
                        className={`relative flex min-w-0 flex-1 items-center justify-center gap-2 rounded-xl px-4 py-2.5 text-sm font-semibold transition-all focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-teal-500 focus-visible:ring-offset-2 sm:flex-initial sm:px-6 ${
                          selected
                            ? "bg-white text-slate-900 shadow-md ring-1 ring-slate-200/80"
                            : "text-slate-500 hover:bg-white/40 hover:text-slate-800"
                        }`}
                      >
                        <i className={`${tab.icon} text-lg opacity-90`} aria-hidden />
                        <span className="hidden sm:inline">{tab.label}</span>
                        <span className="sm:hidden">{tab.short}</span>
                      </button>
                    );
                  })}
                </div>
                <p className="max-w-md text-xs leading-relaxed text-slate-500 sm:text-right">{activeTab.description}</p>
              </div>
              <p className="mt-2 hidden text-[11px] text-slate-400 sm:block">
                On a tab, use <kbd className="rounded border border-slate-200 bg-white px-1 font-mono text-[10px]">←</kbd>{" "}
                <kbd className="rounded border border-slate-200 bg-white px-1 font-mono text-[10px]">→</kbd> to switch scope.
              </p>
            </div>
          </div>

          <div ref={scrollRef} className="min-h-0 flex-1 overflow-y-auto px-4 py-6 sm:px-8 sm:py-8">
            <div className="mx-auto max-w-screen-2xl space-y-6">
            {surfaceError && (
              <div className="flex flex-wrap items-center gap-3 rounded-xl border border-rose-200 bg-rose-50/90 px-4 py-3 text-sm text-rose-900">
                <span>{surfaceError.message}</span>
                <button type="button" className="font-semibold underline" onClick={() => void refresh()}>
                  Retry
                </button>
              </div>
            )}

            {(chatSkeleton || ticketSkeleton || botSkeleton) && (
              <div className="space-y-6">
                <div className="grid animate-pulse grid-cols-1 gap-4 sm:grid-cols-2 xl:grid-cols-4">
                  {[1, 2, 3, 4].map((i) => (
                    <div key={i} className="ui-card h-32 bg-slate-100/80" />
                  ))}
                </div>
                <div className="h-48 animate-pulse rounded-2xl bg-slate-100/80" />
              </div>
            )}

            {(showChatsPanel || showTicketsPanel || showBotPanel) && (
              <div
                key={reportView}
                className="min-h-[calc(100vh-13rem)] overflow-hidden rounded-2xl border border-slate-200/80 bg-white shadow-sm ring-1 ring-slate-900/[0.03] transition-opacity duration-200"
                role="tabpanel"
                id={`report-panel-${reportView}`}
                aria-labelledby={`report-tab-${reportView}`}
              >
                <div
                  className={`h-1 w-full ${
                    reportView === "chats"
                      ? "bg-gradient-to-r from-teal-500 to-teal-400"
                      : reportView === "tickets"
                        ? "bg-gradient-to-r from-amber-500 to-orange-400"
                        : "bg-gradient-to-r from-violet-600 to-violet-400"
                  }`}
                  aria-hidden
                />
                <div className="px-4 py-6 sm:px-8 sm:py-8">
                  {reportView === "chats" && (
                    <>
                      {noWorkspace ? (
                        <div className="mb-6 rounded-xl border border-amber-200 bg-amber-50/90 px-4 py-3 text-sm text-amber-900">
                          Select a workspace to load live chat metrics (same scope as Home).
                        </div>
                      ) : null}
                      <div className="mb-6 flex flex-col justify-between gap-2 border-b border-slate-100 pb-6 sm:flex-row sm:items-end">
                        <div>
                          <h2 className="text-lg font-semibold text-slate-900">Live chat overview</h2>
                          <p className="mt-1 text-sm text-slate-500">
                            KPIs, volume, assignees, and agent ops for the selected date range (UTC).
                          </p>
                        </div>
                        {typeof chatMeta.conversationTotal === "number" && (
                          <p className="text-xs font-medium text-slate-400">
                            {chatMeta.conversationTotal} conversations · Chat SLA from Settings → SLAs
                          </p>
                        )}
                      </div>
                      <KpiGrid items={chatKpis} />
                      <div className="mt-8 grid grid-cols-1 gap-6 xl:grid-cols-3">
                        <VolumeBars
                          title="Chat volume by day"
                          subtitle="Open vs resolved human-agent sessions in the reporting window."
                          days={chatVolumeWeek}
                          max={chatVolumeMax}
                          keys={[
                            { key: "open", className: "bg-teal-600" },
                            { key: "resolved", className: "bg-emerald-600" },
                          ]}
                          legend={[
                            { label: "Open", dot: "bg-teal-600" },
                            { label: "Resolved", dot: "bg-emerald-500 shadow-sm shadow-emerald-500/35" },
                          ]}
                        />
                        <ShareList
                          title="Conversations by outcome"
                          subtitle="Snapshot of all conversations in workspace"
                          rows={chatStatusShare}
                          emptyText="No conversation data."
                        />
                      </div>
                      {volumeEmpty ? (
                        <p className="mt-2 text-xs text-slate-500">
                          No volume rows in this range — try widening the date range or confirm sessions exist for this workspace.
                        </p>
                      ) : null}
                      <div className="mt-8 ui-card">
                        <h3 className="text-base font-semibold text-slate-900">Open chats by assignee</h3>
                        <p className="mt-0.5 text-xs text-slate-500">Who owns active threads right now</p>
                        <div className="mt-6 space-y-3">
                          {assigneeBars.length === 0 ? (
                            <p className="text-sm text-slate-500">No open conversations.</p>
                          ) : (
                            assigneeBars.map((b) => (
                              <div key={b.name}>
                                <div className="mb-1 flex items-center justify-between text-xs font-medium">
                                  <span className="text-slate-700">{b.name}</span>
                                  <span className="tabular-nums text-slate-900">{b.count}</span>
                                </div>
                                <div className="h-2.5 overflow-hidden rounded-full bg-slate-100 ring-1 ring-slate-200/60">
                                  <div
                                    className="h-full rounded-full bg-teal-600 transition-all duration-500"
                                    style={{ width: `${(b.count / maxAssignee) * 100}%` }}
                                  />
                                </div>
                              </div>
                            ))
                          )}
                        </div>
                      </div>
                      <div className="mt-8 ui-card-static overflow-hidden !p-0">
                        <div className="border-b border-slate-100 px-6 py-4">
                          <h3 className="text-base font-semibold text-slate-900">Agents — chat operations</h3>
                          <p className="text-xs text-slate-500">Open chats, inbound transfers, SLA at-risk, persisted SLA breaches</p>
                        </div>
                        <div className="overflow-x-auto">
                          <table className="w-full min-w-[600px]">
                            <thead>
                              <tr className="border-b border-slate-100 bg-slate-50/80 text-left">
                                {["Agent", "Open chats", "First response", "Avg response", "Handoffs in", "Chat SLA at-risk", "SLA breaches", "Max cap"].map((h) => (
                                  <th key={h} className="whitespace-nowrap px-5 py-3 text-[10px] font-bold uppercase tracking-widest text-slate-400">
                                    {h}
                                  </th>
                                ))}
                              </tr>
                            </thead>
                            <tbody className="divide-y divide-slate-100">
                              {agentChatRows.length === 0 ? (
                                <tr>
                                  <td colSpan={8} className="px-5 py-8 text-center text-sm text-slate-500">
                                    No chat assignee activity in this snapshot.
                                  </td>
                                </tr>
                              ) : (
                                agentChatRows.map((a, i) => (
                                  <tr key={a.agentAccountId || `${a.name}-${i}`} className="transition-colors hover:bg-slate-50/80">
                                    <td className="px-5 py-3.5">
                                      <div className="flex items-center gap-3">
                                        <span className="w-6 text-center text-xs font-bold text-slate-300 tabular-nums">{i + 1}</span>
                                        <AgentAvatar name={a.name} />
                                        <span className="text-sm font-semibold text-slate-900">{a.name}</span>
                                      </div>
                                    </td>
                                    <td className="px-5 py-3.5 text-sm font-bold tabular-nums text-slate-800">{a.openChats}</td>
                                    <td className="px-5 py-3.5 text-sm tabular-nums text-slate-700">
                                      {a.firstResponse ? `${a.firstResponse}s` : "—"}
                                    </td>
                                    <td className="px-5 py-3.5 text-sm tabular-nums text-slate-700">
                                      {a.avgResponse ? `${a.avgResponse}s` : "—"}
                                    </td>
                                    <td className="px-5 py-3.5 text-sm tabular-nums text-violet-700">{a.transfersIn}</td>
                                    <td className="px-5 py-3.5 text-sm font-semibold tabular-nums text-rose-700">{a.atRiskChats}</td>
                                    <td className="px-5 py-3.5 text-sm font-semibold tabular-nums text-rose-800">{a.slaBreachCount ?? 0}</td>
                                    <td className="px-5 py-3.5 text-sm text-slate-600 tabular-nums">{a.maxConcurrent}</td>
                                  </tr>
                                ))
                              )}
                            </tbody>
                          </table>
                        </div>
                      </div>

                      <HumanAgentQaSection haQa={chats.haQa} />
                    </>
                  )}

                  {reportView === "tickets" && (
                    <>
                      <div className="mb-6 flex flex-col justify-between gap-2 border-b border-slate-100 pb-6 sm:flex-row sm:items-end">
                        <div>
                          <h2 className="text-lg font-semibold text-slate-900">Ticket queue overview</h2>
                          <p className="mt-1 text-sm text-slate-500">Depth, status mix, and SLA for the ticket backlog.</p>
                        </div>
                        {typeof ticketMeta.ticketTotal === "number" && (
                          <p className="text-xs font-medium text-slate-400">
                            {ticketMeta.ticketTotal} tickets · Ticket SLA from Settings → SLAs
                          </p>
                        )}
                      </div>
                      <KpiGrid items={ticketKpis} />
                      <div className="mt-8 grid grid-cols-1 gap-6 xl:grid-cols-3">
                        <VolumeBars
                          title="Queue trend"
                          subtitle="Daily ticket counts from reporting-service for selected date range."
                          days={ticketVolumeWeek}
                          max={ticketVolumeMax}
                          keys={[
                            { key: "open", className: "bg-teal-600" },
                            { key: "pending", className: "bg-amber-500" },
                            { key: "resolved", className: "bg-emerald-600" },
                          ]}
                          legend={[
                            { label: "Open", dot: "bg-teal-600" },
                            { label: "Pending", dot: "bg-amber-500 shadow-sm shadow-amber-500/35" },
                            { label: "Resolved", dot: "bg-emerald-500 shadow-sm shadow-emerald-500/35" },
                          ]}
                        />
                        <ShareList
                          title="Tickets by status"
                          subtitle="Share of all tickets in the local queue"
                          rows={ticketStatusShare}
                          emptyText="No tickets in queue yet."
                        />
                      </div>
                      {statusCounts.length > 0 && (
                        <div className="mt-8 overflow-hidden rounded-xl border border-slate-100 bg-slate-50/50">
                          <div className="border-b border-slate-200 bg-white px-4 py-2">
                            <h3 className="text-sm font-semibold text-slate-800">Counts by status</h3>
                          </div>
                          <table className="w-full text-sm">
                            <thead>
                              <tr className="border-b border-slate-200 bg-white text-left text-[10px] font-bold uppercase tracking-widest text-slate-400">
                                <th className="px-4 py-2">Status</th>
                                <th className="px-4 py-2 text-right">Count</th>
                              </tr>
                            </thead>
                            <tbody>
                              {statusCounts.map((r) => (
                                <tr key={r.label} className="border-b border-slate-100 bg-white/80 last:border-0">
                                  <td className="px-4 py-2.5 font-medium text-slate-800">{r.label}</td>
                                  <td className="px-4 py-2.5 text-right font-semibold tabular-nums text-slate-900">{r.count}</td>
                                </tr>
                              ))}
                            </tbody>
                          </table>
                        </div>
                      )}
                      <div className="mt-8 ui-card-static overflow-hidden !p-0">
                        <div className="border-b border-slate-100 px-6 py-4">
                          <h3 className="text-base font-semibold text-slate-900">Agents — ticket load</h3>
                          <p className="text-xs text-slate-500">Linked to conversations by assignee · active = not resolved</p>
                        </div>
                        <div className="overflow-x-auto">
                          <table className="w-full min-w-[640px]">
                            <thead>
                              <tr className="border-b border-slate-100 bg-slate-50/80 text-left">
                                {["Agent", "Tickets linked", "Active linked", "Ticket SLA at-risk"].map((h) => (
                                  <th key={h} className="whitespace-nowrap px-5 py-3 text-[10px] font-bold uppercase tracking-widest text-slate-400">
                                    {h}
                                  </th>
                                ))}
                              </tr>
                            </thead>
                            <tbody className="divide-y divide-slate-100">
                              {agentTicketRows.length === 0 ? (
                                <tr>
                                  <td colSpan={4} className="px-5 py-8 text-center text-sm text-slate-500">
                                    No ticket linkage to assignees in this snapshot.
                                  </td>
                                </tr>
                              ) : (
                                agentTicketRows.map((a, i) => (
                                  <tr key={a.name} className="transition-colors hover:bg-slate-50/80">
                                    <td className="px-5 py-3.5">
                                      <div className="flex items-center gap-3">
                                        <span className="w-6 text-center text-xs font-bold text-slate-300 tabular-nums">{i + 1}</span>
                                        <AgentAvatar name={a.name} />
                                        <span className="text-sm font-semibold text-slate-900">{a.name}</span>
                                      </div>
                                    </td>
                                    <td className="px-5 py-3.5 text-sm font-semibold tabular-nums text-slate-800">{a.ticketsLinked}</td>
                                    <td className="px-5 py-3.5 text-sm font-bold tabular-nums text-amber-900">{a.ticketsActiveLinked}</td>
                                    <td className="px-5 py-3.5 text-sm font-semibold tabular-nums text-rose-700">{a.ticketSlaAtRisk}</td>
                                  </tr>
                                ))
                              )}
                            </tbody>
                          </table>
                        </div>
                      </div>
                    </>
                  )}

                  {reportView === "bot" && !botError && (
                    <BotAnalyticsPanel
                      bot={botFromHook}
                      noWorkspace={noWorkspace}
                      onQaComplete={refreshBot}
                    />
                  )}
                </div>
              </div>
            )}
            </div>
          </div>
        </div>
      </div>
    </AppLayout>
  );
}
